import { useState, useEffect } from 'react';
import { Award, Lock, HelpCircle, X } from 'lucide-react';
import { fetchBadges, fetchCustomerShopAccounts, buildClaimBadgeTx } from '../../services/sui-service';
import { signAndExecuteTransaction } from '../../services/zklogin-signer';
import { getSecretBadges, getMySecretBadges, type SecretBadgeRow } from '../../services/collection-service';

// On-chain badge definitions (subset of the old gallery that has live
// contract templates — types without an onchainType can't be earned yet).
const BADGES = [
  { onchainType: 'first_purchase', name: 'First Sip', requirementValue: 1, rarity: 'common' },
  { onchainType: '10_purchases', name: 'Regular', requirementValue: 10, rarity: 'uncommon' },
  { onchainType: '50_purchases', name: 'Connoisseur', requirementValue: 50, rarity: 'rare' },
  { onchainType: '100_purchases', name: 'Coffee Legend', requirementValue: 100, rarity: 'legendary' },
  { onchainType: 'referral_master', name: 'Social Butterfly', requirementValue: 5, rarity: 'rare', referral: true },
] as const;

// Same rarity accents as LootBag's item boxes — grey → teal → purple → gold.
const RARITY_TEXT: Record<string, string> = {
  common: 'text-body-grey', uncommon: 'text-pixel-cyan',
  rare: 'text-pixel-purple', legendary: 'text-gold-ink',
};
const RARITY_BORDER: Record<string, string> = {
  common: 'border-hairline', uncommon: 'border-pixel-cyan',
  rare: 'border-pixel-purple', legendary: 'border-accent-alt',
};
const RARITY_BAR: Record<string, string> = {
  common: 'bg-body-grey', uncommon: 'bg-pixel-cyan',
  rare: 'bg-pixel-purple', legendary: 'bg-accent-alt',
};

// What the info popup shows — either a regular on-chain badge or a secret one.
type Selected =
  | { kind: 'badge'; def: (typeof BADGES)[number]; isEarned: boolean }
  | { kind: 'secret'; def: SecretBadgeRow; isEarned: boolean };

export default function BadgeStrip({ userAddress }: { userAddress: string; embedded?: boolean }) {
  const [earned, setEarned] = useState<string[]>([]);
  const [bestAccountId, setBestAccountId] = useState<string | null>(null);
  const [maxPurchases, setMaxPurchases] = useState(0);
  const [secretDefs, setSecretDefs] = useState<SecretBadgeRow[]>([]);
  const [secretEarned, setSecretEarned] = useState<string[]>([]);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selected | null>(null);

  // Esc closes the popup — mirrors LootBag's item-card overlay (backdrop
  // click + stopPropagation below) plus keyboard support.
  useEffect(() => {
    if (!selected) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setSelected(null); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    (async () => {
      const [badgesRaw, accountsRaw, defs, mine] = await Promise.all([
        fetchBadges(userAddress).catch(() => []),
        fetchCustomerShopAccounts(userAddress).catch(() => []),
        getSecretBadges(),
        getMySecretBadges(userAddress),
      ]);
      if (cancelled) return;
      setEarned(badgesRaw.map((o: any) => (o.data?.content as any)?.fields?.badge_type).filter(Boolean));
      let best: string | null = null; let max = 0;
      accountsRaw.forEach((o: any) => {
        const p = parseInt((o.data?.content as any)?.fields?.purchases) || 0;
        if (p >= max) { max = p; best = o.data?.objectId || null; }
      });
      setMaxPurchases(max); setBestAccountId(best);
      setSecretDefs(defs); setSecretEarned(mine);
    })();
    return () => { cancelled = true; };
  }, [userAddress]);

  async function handleClaim(onchainType: string) {
    if (!bestAccountId || claiming) return;
    setClaiming(onchainType);
    try {
      await signAndExecuteTransaction(buildClaimBadgeTx(bestAccountId, onchainType));
      setEarned(prev => [...prev, onchainType]);
    } catch (err) {
      console.error('Badge claim failed:', err);
    } finally {
      setClaiming(null);
    }
  }

  return (
    <div className="bg-white pixel-corners p-[18px]">
      <p className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey mb-[14px] flex items-center gap-2">
        <Award size={12} /> BADGES
      </p>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {BADGES.map(b => {
          const isEarned = earned.includes(b.onchainType);
          const eligible = !isEarned && !('referral' in b && b.referral) &&
            maxPurchases >= b.requirementValue && !!bestAccountId;
          return (
            // The CLAIM button is a sibling, not a child, of the info button —
            // a <button> can't nest inside another <button> (invalid HTML,
            // and it broke the CLAIM role-query in tests via duplicate names).
            <div key={b.onchainType} className="min-w-[6.5rem] flex flex-col items-center text-center">
              <button
                onClick={() => setSelected({ kind: 'badge', def: b, isEarned })}
                className={`w-full aspect-square pixel-corners border-[3px] flex items-center justify-center ${
                  isEarned ? `${RARITY_BORDER[b.rarity]} bg-white` : 'border-hairline bg-background opacity-70'
                }`}
              >
                {isEarned
                  ? <Award size={22} className={RARITY_TEXT[b.rarity]} />
                  : <Lock size={22} className="text-body-grey" />}
              </button>
              <span className={`font-heading text-[8px] mt-2 ${isEarned ? RARITY_TEXT[b.rarity] : 'text-body-grey'}`}>
                {b.name}
              </span>
              {eligible && (
                <button
                  onClick={() => handleClaim(b.onchainType)}
                  disabled={!!claiming}
                  className="pixel-corners-sm bg-white border-2 border-primary text-primary font-grotesk font-bold text-[10px] px-3 py-1.5 mt-2 disabled:opacity-40"
                >
                  {claiming === b.onchainType ? 'CLAIMING…' : 'CLAIM'}
                </button>
              )}
            </div>
          );
        })}
        {secretDefs.map(s => {
          const isEarned = secretEarned.includes(s.slug);
          return (
            <button key={s.slug}
              onClick={() => setSelected({ kind: 'secret', def: s, isEarned })}
              className={`min-w-[6.5rem] pixel-corners border-[3px] p-3 flex flex-col items-center text-center ${
                isEarned ? 'border-pixel-purple bg-white' : 'border-hairline border-dashed bg-background opacity-70'
              }`}>
              {isEarned
                ? <Award size={22} className="text-pixel-purple" />
                : <HelpCircle size={22} className="text-body-grey" />}
              <span className={`font-heading text-[8px] mt-2 ${isEarned ? 'text-pixel-purple' : 'text-body-grey'}`}>
                {isEarned ? s.name : 'SECRET'}
              </span>
              <span className="font-grotesk font-medium text-[11px] text-body-grey mt-1 leading-tight">
                {isEarned ? s.description : (s.hint || 'Secret — keep brewing.')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Info popup — follows LootBag's item-card overlay pattern */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center safe-modal bg-black/70 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className={`w-full max-w-sm max-h-full overflow-y-auto bg-white pixel-corners border-[3px] p-5 sm:p-8 relative ${
              selected.kind === 'badge'
                ? (selected.isEarned ? RARITY_BORDER[selected.def.rarity] : 'border-hairline')
                : (selected.isEarned ? 'border-pixel-purple' : 'border-hairline')
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setSelected(null)} aria-label="Close" className="absolute top-1 right-1 p-3 text-body-grey hover:text-primary">
              <X size={20} />
            </button>

            {selected.kind === 'badge' ? (
              <>
                <div className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center ${selected.isEarned ? RARITY_TEXT[selected.def.rarity] : 'text-body-grey'}`}>
                  {selected.isEarned ? <Award size={40} /> : <Lock size={40} />}
                </div>
                <p className={`font-heading text-[9px] mb-2 text-center uppercase ${RARITY_TEXT[selected.def.rarity]}`}>
                  {selected.def.rarity}
                </p>
                <h3 className="font-grotesk font-bold text-[17px] text-primary text-center mb-4">{selected.def.name}</h3>
                <p className="font-grotesk font-medium text-[13px] text-body-grey text-center mb-4">
                  {'referral' in selected.def && selected.def.referral
                    ? `Refer ${selected.def.requirementValue} friends who join BitBrew`
                    : `Earn ${selected.def.requirementValue} stamps`}
                </p>
                {selected.isEarned ? (
                  <p className="font-heading text-[9px] text-center text-success">EARNED</p>
                ) : 'referral' in selected.def && selected.def.referral ? (
                  <p className="font-heading text-[9px] text-center text-body-grey">TRACKED AUTOMATICALLY — SHARE YOUR LINK</p>
                ) : (
                  <div>
                    <div className="h-2 pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden">
                      <div
                        className={`h-full ${RARITY_BAR[selected.def.rarity]}`}
                        style={{ width: `${Math.min(100, (maxPurchases / selected.def.requirementValue) * 100)}%` }}
                      />
                    </div>
                    <p className="font-heading text-[9px] text-body-grey text-center mt-2">
                      {maxPurchases} / {selected.def.requirementValue} STAMPS
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center ${selected.isEarned ? 'text-pixel-purple' : 'text-body-grey'}`}>
                  {selected.isEarned ? <Award size={40} /> : <HelpCircle size={40} />}
                </div>
                <h3 className="font-grotesk font-bold text-[17px] text-primary text-center mb-4">
                  {selected.isEarned ? selected.def.name : 'Secret badge'}
                </h3>
                <p className="font-grotesk font-medium text-[13px] text-body-grey text-center mb-4">
                  {selected.isEarned ? selected.def.description : (selected.def.hint || 'Secret — keep brewing.')}
                </p>
                {selected.isEarned && (
                  <p className="font-heading text-[9px] text-center text-success">EARNED</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
